import React, { useState, useEffect, useRef } from "react";
import { FaTimes, FaSave } from "react-icons/fa";
import "./DynamicFormEditableFields.css";
import { sliderNumber, sliderRange, sliderLabel, valueAtPosition, positionOfValue, rangeInputProps } from "../../../utils/sliderField";

const OTHER_MAX = 60;
const isOtherEntry = (val) => val === 'Other' || (typeof val === 'string' && val.startsWith('Other: '));

const EditableField = ({
  fieldName,
  fieldValue,
  characterLimit,
  characterMinimum,
  placeHolder,
  fieldType,
  fieldEnum,
  htmlTitle,
  htmlSubheader,
  htmlSubheaderAlternative,
  htmlref,
  sliderConfig,
  handleSave,
  communityProfileName,
  DisableFields,
  autoSaveOnBlur = false,
  otherBios,
}) => {
  const [value, setValue] = useState(fieldValue);
  const [tempValue, setTempValue] = useState(fieldValue);
  const [isEditing, setIsEditing] = useState(false);
  const [showMinAlert, setShowMinAlert] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
  const inputRef = useRef(null);
  // Set right before Cancel fires (via mousedown/touchstart, which precede blur)
  // so the auto-save-on-blur below doesn't save a value the user is discarding.
  const skipNextBlurSaveRef = useRef(false);

  // For checkbox group
  const [checkboxValues, setCheckboxValues] = useState(
    Array.isArray(fieldValue) ? fieldValue : []
  );

  // For boolean toggle
  const [booleanValue, setBooleanValue] = useState(
    typeof fieldValue === "boolean" ? fieldValue : false
  );

  useEffect(() => {
    if (
      typeof characterMinimum === "number" &&
      fieldValue &&
      fieldValue.length > 0 &&
      fieldValue.length < characterMinimum
    ) {
      setShowMinAlert(true);
    }
  }, [fieldValue, characterMinimum]);

  useEffect(() => {
    if (Array.isArray(fieldValue)) {
      setCheckboxValues(fieldValue);
    }
  }, [fieldValue]);

  useEffect(() => {
    if (typeof fieldValue === "boolean") {
      setBooleanValue(fieldValue);
    }
  }, [fieldValue]);

  // Handle focus after entering edit mode for mobile compatibility
  useEffect(() => {
    if (isEditing && inputRef.current) {
      // Small delay to ensure DOM is updated before focusing
      const focusTimeout = setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          // For iOS Safari, also try setting cursor position
          if (inputRef.current.setSelectionRange) {
            const len = inputRef.current.value.length;
            inputRef.current.setSelectionRange(len, len);
          }
        }
      }, 10);
      return () => clearTimeout(focusTimeout);
    }
  }, [isEditing]);

  const handleChange = (event) => {
    // Only allow changes when in editing mode
    if (!isEditing) {
      return;
    }
    setTempValue(event.target.value);
    if (
      typeof characterMinimum === "number" &&
      !value &&
      !fieldValue &&
      event.target.value.length < characterMinimum
    ) {
      setShowMinAlert(false);
    }
  };

  const startEditing = (event) => {
    if (!DisableFields && !isEditing) {
      // Prevent event bubbling to avoid conflicts
      if (event) {
        event.stopPropagation();
      }
      setIsEditing(true);
      // Focus is handled by useEffect for better mobile compatibility
    }
  };

  const handleSaveClick = () => {
    console.log('💾 [DynamicFormEditableFields] handleSaveClick CALLED');
    console.log('💾 [DynamicFormEditableFields] fieldName:', fieldName);
    console.log('💾 [DynamicFormEditableFields] tempValue:', tempValue);
    console.log('💾 [DynamicFormEditableFields] characterMinimum:', characterMinimum);
    console.log('💾 [DynamicFormEditableFields] handleSave function exists:', typeof handleSave === 'function');
    
    const isTooShort =
      typeof characterMinimum === "number" &&
      tempValue.length > 0 &&
      tempValue.length < characterMinimum;

    console.log('💾 [DynamicFormEditableFields] isTooShort:', isTooShort);

    // Update the field's display value
    setValue(tempValue);
    setIsEditing(false);
    
    // Create synthetic event with the current tempValue (what user actually typed)
    const syntheticEvent = { target: { value: tempValue } };
    
    console.log('💾 [DynamicFormEditableFields] About to call handleSave with tempValue:', tempValue);
    console.log('💾 [DynamicFormEditableFields] Synthetic event value:', syntheticEvent.target.value);
    
    // Call handleSave with the current input value
    handleSave(syntheticEvent, communityProfileName, fieldName);
    
    console.log('💾 [DynamicFormEditableFields] handleSave called');

    if (isTooShort) {
      setShowMinAlert(true);
      setShowModal(true);
    } else {
      setShowMinAlert(false);
      setShowModal(false);
    }
    setShowUnsavedWarning(false);
  };

  const handleCancel = () => {
    setTempValue(value);
    setIsEditing(false);
    setShowMinAlert(
      typeof characterMinimum === "number" &&
      value &&
      value.length > 0 &&
      value.length < characterMinimum
    );
    setShowModal(false);
    setShowUnsavedWarning(false);
  };

  // Belt-and-braces for onboarding only: a user moving through the wizard can
  // type into a field and click the wizard's "Continue" button instead of this
  // field's own "Save" button, thinking the visible text is already saved — the
  // typed value would otherwise never reach the parent form state at all.
  // Auto-save on blur there so leaving the field behaves like clicking Save,
  // unless the user is in the middle of clicking Cancel (skipNextBlurSaveRef
  // guards that case).
  //
  // Regular profile editing (ProfileUpdate/Settings) keeps the existing
  // explicit-Save requirement, since that's an established, understood pattern
  // there — but silently discarding an edit the user clicked away from isn't
  // great either, so show a visible "not saved" warning instead of saving
  // nothing and saying nothing.
  const handleBlur = () => {
    if (skipNextBlurSaveRef.current) {
      skipNextBlurSaveRef.current = false;
      return;
    }
    if (!isEditing) return;

    if (autoSaveOnBlur) {
      handleSaveClick();
      return;
    }

    if (tempValue !== value) {
      setShowUnsavedWarning(true);
    }
  };

  const markSkipNextBlurSave = () => {
    skipNextBlurSaveRef.current = true;
  };

  const handleImportBio = (bio) => {
    setTempValue(bio);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleCheckboxChange = (event, option) => {
    let updated;
    if (event.target.checked) {
      updated = [...checkboxValues, option];
    } else {
      // Unticking "Other" also drops what they typed for it.
      updated = checkboxValues.filter((val) => val !== option && !(option === 'Other' && isOtherEntry(val)));
    }
    setCheckboxValues(updated);

    const syntheticEvent = {
      target: { value: updated }
    };
    handleSave(syntheticEvent, communityProfileName, fieldName, option);
  };

  // A tick-box list whose options include "Other" gets a short text box when
  // it's ticked; the answer is stored in the list itself as "Other: …".
  const otherEntry = checkboxValues.find((val) => typeof val === 'string' && val.startsWith('Other: '));
  const otherTicked = checkboxValues.some(isOtherEntry);
  const handleOtherText = (event) => {
    const text = event.target.value.slice(0, OTHER_MAX);
    const updated = [...checkboxValues.filter((val) => !isOtherEntry(val)), text.trim() ? `Other: ${text}` : 'Other'];
    setCheckboxValues(updated);
    handleSave({ target: { value: updated } }, communityProfileName, fieldName, 'Other');
  };

  const handleBooleanToggle = () => {
    if (!DisableFields) {
      const newValue = !booleanValue;
      setBooleanValue(newValue);
      const syntheticEvent = { target: { value: newValue } };
      handleSave(syntheticEvent, communityProfileName, fieldName);
    }
  };

  const handleSelectChange = (event) => {
    setTempValue(event.target.value);
    handleSave(event, communityProfileName, fieldName);
  };

  const handleModalClose = () => {
    setShowModal(false);
  };

  // Get display label - use htmlTitle if available, otherwise fieldName
  const displayLabel = htmlTitle || fieldName;

  // --- Dynamic feedback logic ---
  const currentLength = (isEditing ? tempValue : value).length;
  const charsToLimit = characterLimit - currentLength;
  const charsToMin =
    typeof characterMinimum === "number"
      ? Math.max(characterMinimum - currentLength, 0)
      : 0;

  const showCharacterCount =
    isEditing &&
    typeof characterLimit === "number" &&
    charsToLimit < 30;

  // 1. Boolean Toggle
  if (fieldType === "boolean" && htmlref === "toggle") {
    return (
      <div key={fieldName} className="boolean-toggle_editableFields">
        <div className="toggle-label_editableFields">{displayLabel}</div>
        <div className="toggle-container_editableFields">
          <label className="toggle-switch_editableFields">
            <input
              type="checkbox"
              checked={booleanValue}
              onChange={handleBooleanToggle}
              disabled={DisableFields}
            />
            <span className="toggle-slider_editableFields"></span>
          </label>
        </div>
        {(htmlSubheader || htmlSubheaderAlternative) && (
          <div className="toggle-subheader_editableFields">
            {booleanValue ? htmlSubheader : htmlSubheaderAlternative}
          </div>
        )}
      </div>
    );
  }

  // 2. Checkbox group
  if (
    Array.isArray(fieldType) &&
    fieldType[0].type === "String" &&
    fieldType[0].htmlref === "checkbox"
  ) {
    return (
      <div key={fieldName} className="checkbox-group_editableFields">
        <div className="checkbox-group-label_editableFields">{displayLabel}</div>
        <div className="checkbox-items_editableFields">
          {fieldType[0].enum.map((option, index) => {
            const checkboxId = `${fieldName}-${index}`;
            return (
              <label key={option} className="checkbox-item_editableFields">
                <input
                  type="checkbox"
                  id={checkboxId}
                  name={fieldName}
                  value={option}
                  checked={option === 'Other' ? otherTicked : checkboxValues.includes(option)}
                  onChange={(event) => handleCheckboxChange(event, option)}
                  disabled={DisableFields}
                />
                <span>{option}</span>
              </label>
            );
          })}
        </div>
        {fieldType[0].enum.includes('Other') && otherTicked && (
          <input
            type="text"
            className="checkbox-other-input_editableFields"
            placeholder="Other — please say what"
            maxLength={OTHER_MAX}
            value={otherEntry ? otherEntry.slice('Other: '.length) : ''}
            onChange={handleOtherText}
            disabled={DisableFields}
            aria-label={`${displayLabel}: other`}
          />
        )}
      </div>
    );
  }

  // 2b. Slider — a number question (htmlref: 'slider'), its range and unit
  // from the schema; the top of the range means "that or further".
  if (htmlref === "slider") {
    const range = sliderRange(sliderConfig);
    const saved = sliderNumber(tempValue, sliderConfig);
    // Unset: the handle rests at the schema's "start" (else the minimum).
    const start = Number.isFinite(Number(sliderConfig?.start)) ? Number(sliderConfig.start) : range.min;
    const shown = saved ?? start;
    // The handle position → the real value (same unless scale: 'curved').
    const commit = (pos) => handleSave({ target: { value: valueAtPosition(pos, sliderConfig) } }, communityProfileName, fieldName);
    return (
      <div key={fieldName} className="slider-container_editableFields">
        <div className="slider-head_editableFields">
          <label className="dropdown-label_editableFields" htmlFor={fieldName}>{displayLabel}</label>
          <span className={`slider-value_editableFields${saved == null ? ' slider-value--unset_editableFields' : ''}`}>
            {saved == null ? `${sliderLabel(start, sliderConfig)} (default — drag to adjust)` : sliderLabel(saved, sliderConfig)}
          </span>
        </div>
        <input
          id={fieldName}
          name={fieldName}
          type="range"
          {...rangeInputProps(sliderConfig)}
          value={positionOfValue(shown, sliderConfig)}
          disabled={DisableFields}
          className={`slider_editableFields${saved == null ? ' slider--unset_editableFields' : ''}`}
          onChange={(e) => setTempValue(valueAtPosition(e.target.value, sliderConfig))}
          onMouseUp={(e) => commit(e.target.value)}
          onTouchEnd={(e) => commit(e.target.value)}
          onKeyUp={(e) => commit(e.target.value)}
          aria-valuetext={sliderLabel(saved ?? shown, sliderConfig)}
        />
        <div className="slider-scale_editableFields" aria-hidden="true">
          <span>{sliderLabel(range.min, sliderConfig)}</span>
          <span>{sliderLabel(range.max, sliderConfig)}</span>
        </div>
      </div>
    );
  }

  // 3. Dropdown
  if (fieldType === "String" && fieldEnum) {
    return (
      <div key={fieldName} className="dropdown-container_editableFields">
        <label className="dropdown-label_editableFields" htmlFor={fieldName}>
          {displayLabel}
        </label>
        <select
          id={fieldName}
          name={fieldName}
          value={tempValue}
          onChange={handleSelectChange}
          disabled={DisableFields}
          className="select_editableFields"
        >
          {/* An answer saved before the options changed (e.g. an old
              handicap band) still shows until they pick a new one. */}
          {tempValue && !fieldEnum.includes(tempValue) && (
            <option key={`current-${tempValue}`} value={tempValue}>
              {tempValue}
            </option>
          )}
          {fieldEnum.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>
    );
  }

  // 4. Textarea 
 // 4. Textarea (long text)
if (fieldType === "String" && typeof characterLimit === "number" && characterLimit >= 100) {
  return (
    <div key={fieldName} className="editable-field-container_editableFields">
      <label htmlFor={fieldName} className="field-label_editableFields">
        {displayLabel}
      </label>

      {/* Character count feedback */}
      <div className="character-feedback-row_editableFields">
        {showCharacterCount && (
          <span className="character-count_editableFields">
            {`${currentLength}/${characterLimit} characters used`}
          </span>
        )}
        {showMinAlert && charsToMin > 0 && (
          <span className="character-min-alert_editableFields construction-alert_editableFields">
            <span role="img" aria-label="construction">👷‍♂️ 🛠️ 🚧</span>
            <span>
              Please write {charsToMin} more characters to complete your {displayLabel}
            </span>
          </span>
        )}
      </div>

      {isEditing && otherBios && otherBios.length > 0 && (
        <div className="bio-import-row_editableFields">
          <span className="bio-import-label_editableFields">Reuse a bio you already wrote:</span>
          {otherBios.map((entry) => (
            <button
              key={entry.communityId}
              type="button"
              className="bio-import-chip_editableFields"
              onMouseDown={markSkipNextBlurSave}
              onTouchStart={markSkipNextBlurSave}
              onClick={() => handleImportBio(entry.bio)}
            >
              From {entry.communityName}
            </button>
          ))}
        </div>
      )}

      <div className="textarea-container_editableFields">
        <textarea
          ref={inputRef}
          id={fieldName}
          name={fieldName}
          value={isEditing ? tempValue : value}
          onChange={handleChange}
          onTouchStart={startEditing}
          onClick={startEditing}
          onBlur={handleBlur}
          maxLength={characterLimit}
          readOnly={false}
          className={`mediumBoxSize_editableFields ${isEditing ? 'editing' : ''}`}
          placeholder={isEditing ? (placeHolder || "Enter text...") : (placeHolder || "Enter text...")}
          disabled={DisableFields}
          inputMode="text"
        />
      </div>

      {showUnsavedWarning && (
        <div className="unsaved-warning_editableFields" role="alert">
          ⚠️ Not saved yet — click Save to keep this change.
        </div>
      )}

      {isEditing && (
        <div className="edit-controls_editableFields buttonContainer_editableFields">
          <button
            id="saveButton"
            className="button"
            onClick={handleSaveClick}
            type="button"
            aria-label="Save"
          >
            <FaSave style={{ marginRight: "0.5em" }} />
            Save
          </button>
          <button
            id="cancelButton"
            className="buttonSecondary"
            onMouseDown={markSkipNextBlurSave}
            onTouchStart={markSkipNextBlurSave}
            onClick={handleCancel}
            type="button"
            aria-label="Cancel"
          >
            <FaTimes style={{ marginRight: "0.5em" }} />
            Cancel
          </button>
        </div>
      )}

      {/* Modal for minimum character requirement */}
      {showModal && (
        <div className="modal-overlay_editableFields">
          <div className="modal-content_editableFields construction-modal_editableFields">
            <span className="emoji-row_editableFields" role="img" aria-label="construction">👷‍♂️ 🛠️ 🚧</span>
            <span className="modal-warning-text_editableFields">
              Please write {charsToMin} more characters to complete your {displayLabel}
            </span>
            <button
              className="buttonSecondary modal-close-btn_editableFields"
              onClick={handleModalClose}
              aria-label="Close"
            >
              <FaTimes style={{ marginRight: "0.5em" }} />
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// 5. Short text input (default)
return (
  <div key={fieldName} className="editable-field-container_editableFields">
    <label htmlFor={fieldName} className="field-label_editableFields">
      {displayLabel}
    </label>

    {/* Character count feedback */}
    <div className="character-feedback-row_editableFields">
      {showCharacterCount && (
        <span className="character-count_editableFields">
          {`${currentLength}/${characterLimit} characters used`}
        </span>
      )}
      {showMinAlert && charsToMin > 0 && (
        <span className="character-min-alert_editableFields construction-alert_editableFields">
          <span role="img" aria-label="construction">👷‍♂️ 🛠️ 🚧</span>
          <span>
            Please write {charsToMin} more characters to complete your {displayLabel}
          </span>
        </span>
      )}
    </div>

    <div className="input-container_editableFields">
      <input
        ref={inputRef}
        type="text"
        id={fieldName}
        name={fieldName}
        value={isEditing ? tempValue : value}
        onChange={handleChange}
        onTouchStart={startEditing}
        onClick={startEditing}
        onBlur={handleBlur}
        maxLength={characterLimit || 100}
        readOnly={false}
        className={`text-input_editableFields ${isEditing ? 'editing' : ''}`}
        placeholder={placeHolder || ""}
        disabled={DisableFields}
        inputMode="text"
      />
    </div>

    {showUnsavedWarning && (
      <div className="unsaved-warning_editableFields" role="alert">
        ⚠️ Not saved yet — click Save to keep this change.
      </div>
    )}

    {isEditing && (
      <div className="edit-controls_editableFields buttonContainer_editableFields">
        <button
          id="saveButton"
          className="button"
          onClick={handleSaveClick}
          type="button"
          aria-label="Save"
        >
          <FaSave style={{ marginRight: "0.5em" }} />
          Save
        </button>
        <button
          id="cancelButton"
          className="buttonSecondary"
          onMouseDown={markSkipNextBlurSave}
          onTouchStart={markSkipNextBlurSave}
          onClick={handleCancel}
          type="button"
          aria-label="Cancel"
        >
          <FaTimes style={{ marginRight: "0.5em" }} />
          Cancel
        </button>
      </div>
    )}

    {/* Modal for minimum character requirement */}
    {showModal && (
      <div className="modal-overlay_editableFields">
        <div className="modal-content_editableFields construction-modal_editableFields">
          <span className="emoji-row_editableFields" role="img" aria-label="construction">👷‍♂️ 🛠️ 🚧</span>
          <span className="modal-warning-text_editableFields">
            Please write {charsToMin} more characters to complete your {displayLabel}
          </span>
          <button
            className="buttonSecondary modal-close-btn_editableFields"
            onClick={handleModalClose}
            aria-label="Close"
          >
            <FaTimes style={{ marginRight: "0.5em" }} />
            Close
          </button>
        </div>
      </div>
    )}
  </div>
);

};

export default EditableField;



// import React, { useState, useEffect, useRef } from "react";
// import { FaTimes, FaSave } from "react-icons/fa";
// import "./EditableFields.css";

// const EditableField = ({
//   fieldName,
//   fieldValue,
//   characterLimit,
//   characterMinimum,
//   placeHolder,
//   fieldType,
//   fieldEnum,
//   handleSave,
//   communityProfileName,
//   DisableFields,
// }) => {
//   const [value, setValue] = useState(fieldValue);
//   const [tempValue, setTempValue] = useState(fieldValue);
//   const [isEditing, setIsEditing] = useState(false);
//   const [showMinAlert, setShowMinAlert] = useState(false);
//   const [showModal, setShowModal] = useState(false);
//   const inputRef = useRef(null);

//   // For checkbox group
//   const [checkboxValues, setCheckboxValues] = useState(
//     Array.isArray(fieldValue) ? fieldValue : []
//   );

//   useEffect(() => {
//     if (
//       typeof characterMinimum === "number" &&
//       fieldValue &&
//       fieldValue.length > 0 &&
//       fieldValue.length < characterMinimum
//     ) {
//       setShowMinAlert(true);
//     }
//   }, [fieldValue, characterMinimum]);

//   useEffect(() => {
//     if (Array.isArray(fieldValue)) {
//       setCheckboxValues(fieldValue);
//     }
//   }, [fieldValue]);

//   const handleChange = (event) => {
//     setTempValue(event.target.value);
//     if (
//       typeof characterMinimum === "number" &&
//       !value &&
//       !fieldValue &&
//       event.target.value.length < characterMinimum
//     ) {
//       setShowMinAlert(false);
//     }
//   };

//   const startEditing = () => {
//     if (!DisableFields) {
//       setIsEditing(true);
//       setTimeout(() => inputRef.current?.focus(), 0);
//     }
//   };

//   const handleSaveClick = () => {
//     const isTooShort =
//       typeof characterMinimum === "number" &&
//       tempValue.length > 0 &&
//       tempValue.length < characterMinimum;

//     setValue(tempValue);
//     setIsEditing(false);
//     const syntheticEvent = { target: { value: tempValue } };
//     handleSave(syntheticEvent, communityProfileName, fieldName);

//     if (isTooShort) {
//       setShowMinAlert(true);
//       setShowModal(true);
//     } else {
//       setShowMinAlert(false);
//       setShowModal(false);
//     }
//   };

//   const handleCancel = () => {
//     setTempValue(value);
//     setIsEditing(false);
//     setShowMinAlert(
//       typeof characterMinimum === "number" &&
//       value &&
//       value.length > 0 &&
//       value.length < characterMinimum
//     );
//     setShowModal(false);
//   };

//   const handleCheckboxChange = (event, option) => {
//     let updated;
//     if (event.target.checked) {
//       updated = [...checkboxValues, option];
//     } else {
//       updated = checkboxValues.filter((val) => val !== option);
//     }
//     setCheckboxValues(updated);

//     const syntheticEvent = {
//       target: { value: updated }
//     };
//     handleSave(syntheticEvent, communityProfileName, fieldName, option);
//   };

//   const handleSelectChange = (event) => {
//     setTempValue(event.target.value);
//     handleSave(event, communityProfileName, fieldName);
//   };

//   const handleModalClose = () => {
//     setShowModal(false);
//   };

//   // --- Dynamic feedback logic ---
//   const currentLength = (isEditing ? tempValue : value).length;
//   const charsToLimit = characterLimit - currentLength;
//   const charsToMin =
//     typeof characterMinimum === "number"
//       ? Math.max(characterMinimum - currentLength, 0)
//       : 0;

//   const showCharacterCount =
//     isEditing &&
//     typeof characterLimit === "number" &&
//     charsToLimit < 30;

//   // 1. Checkbox group
//   if (
//     Array.isArray(fieldType) &&
//     fieldType[0].type === "String" &&
//     fieldType[0].htmlref === "checkbox"
//   ) {
//     return (
//       <div key={fieldName} className="checkbox-group_editableFields">
//         <div className="checkbox-group-label_editableFields">{fieldName}</div>
//         <div className="checkbox-items_editableFields">
//           {fieldType[0].enum.map((option, index) => {
//             const checkboxId = `${fieldName}-${index}`;
//             return (
//               <label key={option} className="checkbox-item_editableFields">
//                 <input
//                   type="checkbox"
//                   id={checkboxId}
//                   name={fieldName}
//                   value={option}
//                   checked={checkboxValues.includes(option)}
//                   onChange={(event) => handleCheckboxChange(event, option)}
//                   disabled={DisableFields}
//                 />
//                 <span>{option}</span>
//               </label>
//             );
//           })}
//         </div>
//       </div>
//     );
//   }

//   // 2. Select dropdown
//   if (fieldType === "String" && Array.isArray(fieldEnum)) {
//     return (
//       <div key={fieldName} className="dropdown-container_editableFields">
//         <label htmlFor={fieldName} className="dropdown-label_editableFields">{fieldName}</label>
//         <select
//           id={fieldName}
//           name={fieldName}
//           value={tempValue}
//           onChange={handleSelectChange}
//           disabled={DisableFields}
//           className="select_editableFields"
//         >
//           {fieldEnum.map((option) => (
//             <option key={option} value={option}>
//               {option}
//             </option>
//           ))}
//         </select>
//       </div>
//     );
//   }

//   // Textarea or text input rendering
//   return (
//     <div key={fieldName} className="editable-field-container_editableFields">
//       <label htmlFor={fieldName} className="field-label_editableFields">{fieldName}</label>

//       {/* Character count feedback */}
//       <div className="character-feedback-row_editableFields">
//         {showCharacterCount && (
//           <span className="character-count_editableFields">
//             {`${currentLength}/${characterLimit} characters used`}
//           </span>
//         )}
//         {showMinAlert && charsToMin > 0 && (
//           <span className="character-min-alert_editableFields construction-alert_editableFields">
//             <span role="img" aria-label="construction">👷‍♂️ 🛠️ 🚧</span>
//             <span>
//               Please write {charsToMin} more characters to complete your {fieldName}
//             </span>
//           </span>
//         )}
//       </div>

//       {characterLimit >= 1000 ? (
//         <div className="textarea-container_editableFields">
//           <textarea
//             ref={inputRef}
//             id={fieldName}
//             name={fieldName}
//             value={isEditing ? tempValue : value}
//             onChange={handleChange}
//             onClick={startEditing}
//             onFocus={startEditing}
//             maxLength={characterLimit}
//             readOnly={!isEditing}
//             className={`mediumBoxSize_editableFields ${isEditing ? 'editing' : ''}`}
//             placeholder={placeHolder || "Introduce yourself..."}
//           />
//         </div>
//       ) : (
//         <div className="input-container_editableFields">
//           <input
//             ref={inputRef}
//             type="text"
//             id={fieldName}
//             name={fieldName}
//             value={isEditing ? tempValue : value}
//             onChange={handleChange}
//             onClick={startEditing}
//             onFocus={startEditing}
//             maxLength={20}
//             readOnly={!isEditing}
//             className={`text-input_editableFields ${isEditing ? 'editing' : ''}`}
//             placeholder={placeHolder}
//           />
//         </div>
//       )}

//       {isEditing && (
//         <div className="edit-controls_editableFields buttonContainer_editableFields">
//           <button
//             className="button"
//             onClick={handleSaveClick}
//             type="button"
//             aria-label="Save"
//           >
//             <FaSave style={{ marginRight: "0.5em" }} />
//             Save
//           </button>
//           <button
//             className="buttonSecondary"
//             onClick={handleCancel}
//             type="button"
//             aria-label="Cancel"
//           >
//             <FaTimes style={{ marginRight: "0.5em" }} />
//             Cancel
//           </button>
//         </div>
//       )}

//       {/* Modal for minimum character requirement */}
//       {showModal && (
//         <div className="modal-overlay_editableFields">
//           <div className="modal-content_editableFields construction-modal_editableFields">
//             <span className="emoji-row_editableFields" role="img" aria-label="construction">👷‍♂️ 🛠️ 🚧</span>
//             <span className="modal-warning-text_editableFields">
//               Please write {charsToMin} more characters to complete your {fieldName}
//             </span>
//             <button
//               className="buttonSecondary modal-close-btn_editableFields"
//               onClick={handleModalClose}
//               aria-label="Close"
//             >
//               <FaTimes style={{ marginRight: "0.5em" }} />
//               Close
//             </button>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default EditableField;








// import React, { useState, useEffect, useRef } from "react";
// import { FaTimes, FaSave } from "react-icons/fa";

// const EditableField = ({
//   fieldName,
//   fieldValue,
//   characterLimit,
//   characterMinimum,
//   placeHolder,
//   fieldType,
//   fieldEnum,
//   handleSave,
//   communityProfileName,
//   DisableFields,
// }) => {
//   const [value, setValue] = useState(fieldValue);
//   const [tempValue, setTempValue] = useState(fieldValue);
//   const [isEditing, setIsEditing] = useState(false);
//   const [showMinAlert, setShowMinAlert] = useState(false);
//   const [showModal, setShowModal] = useState(false);
//   const inputRef = useRef(null);

//   // For checkbox group
//   const [checkboxValues, setCheckboxValues] = useState(
//     Array.isArray(fieldValue) ? fieldValue : []
//   );

//   // Show warning on mount if initial value exists and is too short
//   useEffect(() => {
//     if (
//       typeof characterMinimum === "number" &&
//       fieldValue &&
//       fieldValue.length > 0 &&
//       fieldValue.length < characterMinimum
//     ) {
//       setShowMinAlert(true);
//     }
//   }, [fieldValue, characterMinimum]);

//   useEffect(() => {
//     if (Array.isArray(fieldValue)) {
//       setCheckboxValues(fieldValue);
//     }
//   }, [fieldValue]);

//   const handleChange = (event) => {
//     setTempValue(event.target.value);
//     // Do not show warning while typing if started from empty
//     if (
//       typeof characterMinimum === "number" &&
//       !value &&
//       !fieldValue &&
//       event.target.value.length < characterMinimum
//     ) {
//       setShowMinAlert(false);
//     }
//   };

//   const handleSelectChange = (event) => {
//     setTempValue(event.target.value);
//     handleSave(event, communityProfileName, fieldName);
//   };

//   const handleCheckboxChange = (event, option) => {
//     let updated;
//     if (event.target.checked) {
//       updated = [...checkboxValues, option];
//     } else {
//       updated = checkboxValues.filter((val) => val !== option);
//     }
//     setCheckboxValues(updated);

//     const syntheticEvent = {
//       target: { value: updated }
//     };
//     handleSave(syntheticEvent, communityProfileName, fieldName, option);
//   };

//   const startEditing = () => {
//     if (!DisableFields) {
//       setIsEditing(true);
//       setTimeout(() => inputRef.current?.focus(), 0);
//     }
//   };

//   const handleSaveClick = () => {
//     const isTooShort =
//       typeof characterMinimum === "number" &&
//       tempValue.length > 0 &&
//       tempValue.length < characterMinimum;

//     setValue(tempValue);
//     setIsEditing(false);
//     const syntheticEvent = { target: { value: tempValue } };
//     handleSave(syntheticEvent, communityProfileName, fieldName);

//     if (isTooShort) {
//       setShowMinAlert(true);
//       setShowModal(true);
//     } else {
//       setShowMinAlert(false);
//       setShowModal(false);
//     }
//   };

//   const handleCancel = () => {
//     setTempValue(value);
//     setIsEditing(false);
//     setShowMinAlert(
//       typeof characterMinimum === "number" &&
//       value &&
//       value.length > 0 &&
//       value.length < characterMinimum
//     );
//     setShowModal(false);
//   };

//   const handleModalClose = () => {
//     setShowModal(false);
//   };

//   // --- Dynamic feedback logic ---
//   const currentLength = (isEditing ? tempValue : value).length;
//   const charsToLimit = characterLimit - currentLength;
//   const charsToMin =
//     typeof characterMinimum === "number"
//       ? Math.max(characterMinimum - currentLength, 0)
//       : 0;

//   const showCharacterCount =
//     isEditing &&
//     typeof characterLimit === "number" &&
//     charsToLimit < 30;

//   // 1. Checkbox group
//   if (
//     Array.isArray(fieldType) &&
//     fieldType[0].type === "String" &&
//     fieldType[0].htmlref === "checkbox"
//   ) {
//     return (
//       <div key={fieldName} className="dynamic-form-checkboxes">
//         <label className="dynamic-form__checkbox-label">{fieldName}</label>
//         {fieldType[0].enum.map((option, index) => {
//           const checkboxId = `${fieldName}-${index}`;
//           return (
//             <div key={option} className="dynamic-form">
//               <input
//                 type="checkbox"
//                 id={checkboxId}
//                 name={fieldName}
//                 value={option}
//                 checked={checkboxValues.includes(option)}
//                 onChange={(event) => handleCheckboxChange(event, option)}
//                 disabled={DisableFields}
//               />
//               <label
//                 id="checkBoxItems"
//                 htmlFor={checkboxId}
//                 className="dynamic-form__checkbox-label"
//               >
//                 {option}
//               </label>
//             </div>
//           );
//         })}
//       </div>
//     );
//   }

//   // 2. Select dropdown
//   if (fieldType === "String" && Array.isArray(fieldEnum)) {
//     return (
//       <div key={fieldName}>
//         <label htmlFor={fieldName}>{fieldName}</label>
//         <select
//           id={fieldName}
//           name={fieldName}
//           value={tempValue}
//           onChange={handleSelectChange}
//           disabled={DisableFields}
//         >
//           {fieldEnum.map((option) => (
//             <option key={option} value={option}>
//               {option}
//             </option>
//           ))}
//         </select>
//       </div>
//     );
//   }

//   // 3. Textarea or text input
//   return (
//     <div key={fieldName} className="editable-field-container">
//       <label htmlFor={fieldName}>{fieldName}</label>

//       {/* Character count and minimum alert */}
//       <div className="character-feedback-row">
//         {showCharacterCount && (
//           <span className="character-count">
//             {`${currentLength}/${characterLimit} characters used`}
//           </span>
//         )}
//         {/* Inline warning if loaded with insufficient text, or after save click */}
//         {showMinAlert && charsToMin > 0 && (
//           <span className="character-min-alert construction-alert">
//             <span role="img" aria-label="construction">👷‍♂️ 🛠️ 🚧</span>
//             <span>
//               Please write {charsToMin} more characters to complete your {fieldName}
//             </span>
//           </span>
//         )}
//       </div>

//       {characterLimit >= 1000 ? (
//         <div className="textarea-container">
//           <textarea
//             ref={inputRef}
//             id={fieldName}
//             name={fieldName}
//             value={isEditing ? tempValue : value}
//             onChange={handleChange}
//             onClick={startEditing}
//             maxLength={characterLimit}
//             readOnly={!isEditing}
//             className={`mediumBoxSize ${isEditing ? 'editing' : ''}`}
//             placeholder={placeHolder || "Introduce yourself and who you'd like to connect with and why..."}
//           />
//         </div>
//       ) : (
//         <div className="input-container">
//           <input
//             ref={inputRef}
//             type="text"
//             id={fieldName}
//             name={fieldName}
//             value={isEditing ? tempValue : value}
//             onChange={handleChange}
//             onClick={startEditing}
//             maxLength={20}
//             readOnly={!isEditing}
//             className={isEditing ? 'editing' : ''}
//             placeholder={placeHolder}
//           />
//         </div>
//       )}

//       {isEditing && (
//         <div className="edit-controls buttonContainer">
//           <button
//             className="button"
//             onClick={handleSaveClick}
//             type="button"
//             aria-label="Save"
//           >
//             <FaSave style={{ marginRight: "0.5em" }} />
//             Save
//           </button>
//           <button
//             className="buttonSecondary"
//             onClick={handleCancel}
//             type="button"
//             aria-label="Cancel"
//           >
//             <FaTimes style={{ marginRight: "0.5em" }} />
//             Cancel
//           </button>
//         </div>
//       )}

//       {/* Modal for minimum character requirement */}
//       {showModal && (
//         <div className="modal-overlay">
//           <div className="modal-content construction-modal">
//             <span className="emoji-row" role="img" aria-label="construction">👷‍♂️ 🛠️ 🚧</span>
//             <span className="modal-warning-text">
//               Please write {charsToMin} more characters to complete your {fieldName}
//             </span>
//             <button
//               className="buttonSecondary modal-close-btn"
//               onClick={handleModalClose}
//               aria-label="Close"
//             >
//               <FaTimes style={{ marginRight: "0.5em" }} />
//               Close
//             </button>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default EditableField;


// import React, { useState, useEffect, useRef } from "react";
// import "./EditableFields.css"

// const EditableField = ({
//   fieldName,
//   fieldValue,
//   characterLimit,
//   characterMinimum,
//   placeHolder,
//   fieldType,
//   fieldEnum,
//   handleSave,
//   communityProfileName,
//   DisableFields,
// }) => {
//   const [value, setValue] = useState(fieldValue);
//   const [tempValue, setTempValue] = useState(fieldValue);
//   const [isEditing, setIsEditing] = useState(false);
//   const [showMinAlert, setShowMinAlert] = useState(false);
//   const [showModal, setShowModal] = useState(false);
//   const inputRef = useRef(null);

//   // For checkbox group
//   const [checkboxValues, setCheckboxValues] = useState(
//     Array.isArray(fieldValue) ? fieldValue : []
//   );

//   // Show warning on mount if initial value exists and is too short
//   useEffect(() => {
//     if (
//       typeof characterMinimum === "number" &&
//       fieldValue &&
//       fieldValue.length > 0 &&
//       fieldValue.length < characterMinimum
//     ) {
//       setShowMinAlert(true);
//     }
//   }, [fieldValue, characterMinimum]);

//   useEffect(() => {
//     if (Array.isArray(fieldValue)) {
//       setCheckboxValues(fieldValue);
//     }
//   }, [fieldValue]);

//   const handleChange = (event) => {
//     setTempValue(event.target.value);
//     // Do not show warning while typing if started from empty
//     if (
//       typeof characterMinimum === "number" &&
//       !value &&
//       !fieldValue &&
//       event.target.value.length < characterMinimum
//     ) {
//       setShowMinAlert(false);
//     }
//   };

//   const handleSelectChange = (event) => {
//     setTempValue(event.target.value);
//     handleSave(event, communityProfileName, fieldName);
//   };

//   const handleCheckboxChange = (event, option) => {
//     let updated;
//     if (event.target.checked) {
//       updated = [...checkboxValues, option];
//     } else {
//       updated = checkboxValues.filter((val) => val !== option);
//     }
//     setCheckboxValues(updated);

//     const syntheticEvent = {
//       target: { value: updated }
//     };
//     handleSave(syntheticEvent, communityProfileName, fieldName, option);
//   };

//   const startEditing = () => {
//     if (!DisableFields) {
//       setIsEditing(true);
//       setTimeout(() => inputRef.current?.focus(), 0);
//     }
//   };

//   const handleSaveClick = () => {
//     const isTooShort =
//       typeof characterMinimum === "number" &&
//       tempValue.length > 0 &&
//       tempValue.length < characterMinimum;

//     setValue(tempValue);
//     setIsEditing(false);
//     const syntheticEvent = { target: { value: tempValue } };
//     handleSave(syntheticEvent, communityProfileName, fieldName);

//     if (isTooShort) {
//       setShowMinAlert(true);
//       setShowModal(true);
//     } else {
//       setShowMinAlert(false);
//       setShowModal(false);
//     }
//   };

//   const handleCancel = () => {
//     setTempValue(value);
//     setIsEditing(false);
//     setShowMinAlert(
//       typeof characterMinimum === "number" &&
//       value &&
//       value.length > 0 &&
//       value.length < characterMinimum
//     );
//     setShowModal(false);
//   };

//   const handleModalClose = () => {
//     setShowModal(false);
//   };

//   // --- Dynamic feedback logic ---
//   const currentLength = (isEditing ? tempValue : value).length;
//   const charsToLimit = characterLimit - currentLength;
//   const charsToMin =
//     typeof characterMinimum === "number"
//       ? Math.max(characterMinimum - currentLength, 0)
//       : 0;

//   const showCharacterCount =
//     isEditing &&
//     typeof characterLimit === "number" &&
//     charsToLimit < 30;

//   // 1. Checkbox group
//   if (
//     Array.isArray(fieldType) &&
//     fieldType[0].type === "String" &&
//     fieldType[0].htmlref === "checkbox"
//   ) {
//     return (
//       <div key={fieldName} className="dynamic-form-checkboxes">
//         <label className="dynamic-form__checkbox-label">{fieldName}</label>
//         {fieldType[0].enum.map((option, index) => {
//           const checkboxId = `${fieldName}-${index}`;
//           return (
//             <div key={option} className="dynamic-form">
//               <input
//                 type="checkbox"
//                 id={checkboxId}
//                 name={fieldName}
//                 value={option}
//                 checked={checkboxValues.includes(option)}
//                 onChange={(event) => handleCheckboxChange(event, option)}
//                 disabled={DisableFields}
//               />
//               <label
//                 id="checkBoxItems"
//                 htmlFor={checkboxId}
//                 className="dynamic-form__checkbox-label"
//               >
//                 {option}
//               </label>
//             </div>
//           );
//         })}
//       </div>
//     );
//   }

//   // 2. Select dropdown
//   if (fieldType === "String" && Array.isArray(fieldEnum)) {
//     return (
//       <div key={fieldName}>
//         <label htmlFor={fieldName}>{fieldName}</label>
//         <select
//           id={fieldName}
//           name={fieldName}
//           value={tempValue}
//           onChange={handleSelectChange}
//           disabled={DisableFields}
//         >
//           {fieldEnum.map((option) => (
//             <option key={option} value={option}>
//               {option}
//             </option>
//           ))}
//         </select>
//       </div>
//     );
//   }

//   // 3. Textarea or text input
//   return (
//     <div key={fieldName} className="editable-field-container">
//       <label htmlFor={fieldName}>{fieldName}</label>

//       {/* Character count and minimum alert */}
//       <div className="character-feedback-row">
//         {showCharacterCount && (
//           <span className="character-count">
//             {`${currentLength}/${characterLimit} characters used`}
//           </span>
//         )}
//         {/* Inline warning if loaded with insufficient text, or after save click */}
//         {showMinAlert && charsToMin > 0 && (
//           <span className="character-min-alert construction-alert">
//             <span role="img" aria-label="construction">👷‍♂️ 🛠️ 🚧</span>
//             <span>
//                Please write {charsToMin} more characters {/* to complete your {fieldName}*/}
//             </span>
//           </span>
//         )}
//       </div>

//       {characterLimit >= 1000 ? (
//         <div className="textarea-container">
//           <textarea
//             ref={inputRef}
//             id={fieldName}
//             name={fieldName}
//             value={isEditing ? tempValue : value}
//             onChange={handleChange}
//             onClick={startEditing}
//             maxLength={characterLimit}
//             readOnly={!isEditing}
//             className={`mediumBoxSize ${isEditing ? 'editing' : ''}`}
//             placeholder={placeHolder || "Introduce yourself and who you'd like to connect with and why..."}
//           />
//         </div>
//       ) : (
//         <div className="input-container">
//           <input
//             ref={inputRef}
//             type="text"
//             id={fieldName}
//             name={fieldName}
//             value={isEditing ? tempValue : value}
//             onChange={handleChange}
//             onClick={startEditing}
//             maxLength={20}
//             readOnly={!isEditing}
//             className={isEditing ? 'editing' : ''}
//             placeholder={placeHolder}
//           />
//         </div>
//       )}

//       {isEditing && (
//         <div className="edit-controls buttonContainer">
//           <button
//             className="button"
//             onClick={handleSaveClick}
//             type="button"
//             aria-label="Save"
//           >
//             Save
//           </button>
//           <button
//             className="buttonSecondary"
//             onClick={handleCancel}
//             type="button"
//             aria-label="Cancel"
//           >
//             Cancel
//           </button>
//         </div>
//       )}

//       {/* Modal for minimum character requirement */}
//       {showModal && (
//         <div className="modal-overlay">
//           <div className="modal-content construction-modal">
//             <span className="emoji-row" role="img" aria-label="construction">👷‍♂️ 🛠️ 🚧</span>
//             <span className="modal-warning-text">
//               Please write {charsToMin} more characters {/* to complete your {fieldName}*/}
//             </span>
//             <button
//               className="buttonSecondary modal-close-btn"
//               onClick={handleModalClose}
//               aria-label="Close"
//             >
//               Close
//             </button>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default EditableField;


// //EditableFields.js
  // import React, { useState, useEffect, useRef } from "react";
  // import { FaTimes, FaSave } from "react-icons/fa";

  // const EditableField = ({
  //   fieldName,
  //   fieldValue,
  //   characterLimit,
  //   characterMinimum,
  //   placeHolder,
  //   fieldType,
  //   fieldEnum,
  //   handleSave,
  //   communityProfileName,
  //   DisableFields,
  // }) => {
  //   const [value, setValue] = useState(fieldValue);
  //   const [tempValue, setTempValue] = useState(fieldValue);
  //   const [isEditing, setIsEditing] = useState(false);
  //   const [showModal, setShowModal] = useState(false);
  //   const inputRef = useRef(null);

  //   // For checkbox group
  //   const [checkboxValues, setCheckboxValues] = useState(
  //     Array.isArray(fieldValue) ? fieldValue : []
  //   );

  //   useEffect(() => {
  //     if (Array.isArray(fieldValue)) {
  //       setCheckboxValues(fieldValue);
  //     }
  //   }, [fieldValue]);

  //   const handleChange = (event) => {
  //     setTempValue(event.target.value);
  //   };

  //   const handleSelectChange = (event) => {
  //     setTempValue(event.target.value);
  //     handleSave(event, communityProfileName, fieldName);
  //   };

  //   const handleCheckboxChange = (event, option) => {
  //     let updated;
  //     if (event.target.checked) {
  //       updated = [...checkboxValues, option];
  //     } else {
  //       updated = checkboxValues.filter((val) => val !== option);
  //     }
  //     setCheckboxValues(updated);

  //     const syntheticEvent = {
  //       target: { value: updated }
  //     };
  //     handleSave(syntheticEvent, communityProfileName, fieldName, option);
  //   };

  //   const startEditing = () => {
  //     if (!DisableFields) {
  //       setIsEditing(true);
  //       setTimeout(() => inputRef.current?.focus(), 0);
  //     }
  //   };

  //   const handleSaveClick = () => {
  //     // Only show modal if minimum is not met and user has typed something
  //     if (
  //       typeof characterMinimum === "number" &&
  //       tempValue.length > 0 &&
  //       tempValue.length < characterMinimum
  //     ) {
  //       setShowModal(true);
  //       return;
  //     }
  //     setValue(tempValue);
  //     setIsEditing(false);
  //     const syntheticEvent = { target: { value: tempValue } };
  //     handleSave(syntheticEvent, communityProfileName, fieldName);
  //   };

  //   const handleCancel = () => {
  //     setTempValue(value);
  //     setIsEditing(false);
  //   };

  //   const handleModalClose = () => {
  //     setShowModal(false);
  //   };

  //   // --- Dynamic feedback logic ---
  //   const currentLength = (isEditing ? tempValue : value).length;
  //   const charsToLimit = characterLimit - currentLength;
  //   const showCharacterCount =
  //     isEditing &&
  //     typeof characterLimit === "number" &&
  //     charsToLimit < 30 &&
  //     currentLength >= (typeof characterMinimum === "number" ? characterMinimum : 0);

  //   // 1. Checkbox group
  //   if (
  //     Array.isArray(fieldType) &&
  //     fieldType[0].type === "String" &&
  //     fieldType[0].htmlref === "checkbox"
  //   ) {
  //     return (
  //       <div key={fieldName} className="dynamic-form-checkboxes">
  //         <label className="dynamic-form__checkbox-label">{fieldName}</label>
  //         {fieldType[0].enum.map((option, index) => {
  //           const checkboxId = `${fieldName}-${index}`;
  //           return (
  //             <div key={option} className="dynamic-form">
  //               <input
  //                 type="checkbox"
  //                 id={checkboxId}
  //                 name={fieldName}
  //                 value={option}
  //                 checked={checkboxValues.includes(option)}
  //                 onChange={(event) => handleCheckboxChange(event, option)}
  //                 disabled={DisableFields}
  //               />
  //               <label
  //                 id="checkBoxItems"
  //                 htmlFor={checkboxId}
  //                 className="dynamic-form__checkbox-label"
  //               >
  //                 {option}
  //               </label>
  //             </div>
  //           );
  //         })}
  //       </div>
  //     );
  //   }

  //   // 2. Select dropdown
  //   if (fieldType === "String" && Array.isArray(fieldEnum)) {
  //     return (
  //       <div key={fieldName}>
  //         <label htmlFor={fieldName}>{fieldName}</label>
  //         <select
  //           id={fieldName}
  //           name={fieldName}
  //           value={tempValue}
  //           onChange={handleSelectChange}
  //           disabled={DisableFields}
  //         >
  //           {fieldEnum.map((option) => (
  //             <option key={option} value={option}>
  //               {option}
  //             </option>
  //           ))}
  //         </select>
  //       </div>
  //     );
  //   }

  //   // 3. Textarea or text input
  //   return (
  //     <div key={fieldName} className="editable-field-container">
  //       <label htmlFor={fieldName}>{fieldName}</label>

  //       {/* Character count only when near the limit */}
  //       <div className="character-feedback-row">
  //         {showCharacterCount && (
  //           <span className="character-count">
  //             {`${currentLength}/${characterLimit} characters used`}
  //           </span>
  //         )}
  //       </div>

  //       {characterLimit >= 1000 ? (
  //         <div className="textarea-container">
  //           <textarea
  //             ref={inputRef}
  //             id={fieldName}
  //             name={fieldName}
  //             value={isEditing ? tempValue : value}
  //             onChange={handleChange}
  //             onClick={startEditing}
  //             maxLength={characterLimit}
  //             readOnly={!isEditing}
  //             className={`mediumBoxSize ${isEditing ? 'editing' : ''}`}
  //             placeholder={placeHolder || "Introduce yourself and who you'd like to connect with and why..."}
  //           />
  //         </div>
  //       ) : (
  //         <div className="input-container">
  //           <input
  //             ref={inputRef}
  //             type="text"
  //             id={fieldName}
  //             name={fieldName}
  //             value={isEditing ? tempValue : value}
  //             onChange={handleChange}
  //             onClick={startEditing}
  //             maxLength={20}
  //             readOnly={!isEditing}
  //             className={isEditing ? 'editing' : ''}
  //             placeholder={placeHolder}
  //           />
  //         </div>
  //       )}

  //       {isEditing && (
  //         <div className="edit-controls">
  //           <button className="icon-button" onClick={handleSaveClick} type="button" aria-label="Save">
  //             <FaSave />
  //           </button>
  //           <button className="icon-button" onClick={handleCancel} type="button" aria-label="Discard">
  //             <FaTimes />
  //           </button>
  //         </div>
  //       )}

  //       {/* Modal/Banner for minimum character requirement */}
  //       {showModal && (
  //         <div className="modal-overlay">
  //           <div className="modal-content">
  //             <span>
  //               Bios must be at least {characterMinimum} characters long.
  //             </span>
  //             <button className="icon-button" onClick={handleModalClose} aria-label="Close" style={{marginLeft: '1em'}}>
  //               <FaTimes />
  //             </button>
  //           </div>
  //         </div>
  //       )}
  //     </div>
  //   );
  // };

  // export default EditableField;

// import React, { useState, useEffect, useRef } from "react";
// import { FaTimes, FaSave } from "react-icons/fa";

// const EditableField = ({
//   fieldName,
//   fieldValue,
//   characterLimit,
//   characterMinimum,
//   placeHolder,
//   fieldType,
//   fieldEnum,
//   handleSave,
//   communityProfileName,
//   DisableFields,
// }) => {
//   const [value, setValue] = useState(fieldValue);
//   const [tempValue, setTempValue] = useState(fieldValue);
//   const [isEditing, setIsEditing] = useState(false);
//   const [showWarning, setShowWarning] = useState(false);
//   const inputRef = useRef(null);

//   // For checkbox group
//   const [checkboxValues, setCheckboxValues] = useState(
//     Array.isArray(fieldValue) ? fieldValue : []
//   );

//   useEffect(() => {
//     if (Array.isArray(fieldValue)) {
//       setCheckboxValues(fieldValue);
//     }
//   }, [fieldValue]);

//   const handleChange = (event) => {
//     setTempValue(event.target.value);
//   };

//   const handleSelectChange = (event) => {
//     setTempValue(event.target.value);
//     handleSave(event, communityProfileName, fieldName);
//   };

//   const handleCheckboxChange = (event, option) => {
//     let updated;
//     if (event.target.checked) {
//       updated = [...checkboxValues, option];
//     } else {
//       updated = checkboxValues.filter((val) => val !== option);
//     }
//     setCheckboxValues(updated);

//     const syntheticEvent = {
//       target: { value: updated }
//     };
//     handleSave(syntheticEvent, communityProfileName, fieldName, option);
//   };

//   const startEditing = () => {
//     if (!DisableFields) {
//       setIsEditing(true);
//       setTimeout(() => inputRef.current?.focus(), 0);
//     }
//   };

//   const handleSaveClick = () => {
//     setValue(tempValue);
//     setIsEditing(false);
//     const syntheticEvent = { target: { value: tempValue } };
//     handleSave(syntheticEvent, communityProfileName, fieldName);
//   };

//   const handleCancel = () => {
//     setTempValue(value);
//     setIsEditing(false);
//   };

//   // --- Dynamic feedback logic ---
//   const currentLength = (isEditing ? tempValue : value).length;
//   const charsLeft = Math.max(
//     0,
//     typeof characterMinimum === "number" ? characterMinimum - currentLength : 0
//   );
//   const shouldShowWarning =
//     typeof characterMinimum === "number" &&
//     currentLength > 0 &&
//     currentLength < characterMinimum;

//   // For fading out the warning
//   useEffect(() => {
//     if (!shouldShowWarning && showWarning) {
//       // Fade out after 500ms
//       const timeout = setTimeout(() => setShowWarning(false), 500);
//       return () => clearTimeout(timeout);
//     }
//     if (shouldShowWarning && !showWarning) {
//       setShowWarning(true);
//     }
//   }, [shouldShowWarning, showWarning]);

//   // Show x/y character count only when within 30 of the limit
//   const charsToLimit = characterLimit - currentLength;
//   const showCharacterCount =
//     isEditing &&
//     typeof characterLimit === "number" &&
//     charsToLimit < 30 &&
//     currentLength >= characterMinimum;

//   // 1. Checkbox group
//   if (
//     Array.isArray(fieldType) &&
//     fieldType[0].type === "String" &&
//     fieldType[0].htmlref === "checkbox"
//   ) {
//     return (
//       <div key={fieldName} className="dynamic-form-checkboxes">
//         <label className="dynamic-form__checkbox-label">{fieldName}</label>
//         {fieldType[0].enum.map((option, index) => {
//           const checkboxId = `${fieldName}-${index}`;
//           return (
//             <div key={option} className="dynamic-form">
//               <input
//                 type="checkbox"
//                 id={checkboxId}
//                 name={fieldName}
//                 value={option}
//                 checked={checkboxValues.includes(option)}
//                 onChange={(event) => handleCheckboxChange(event, option)}
//                 disabled={DisableFields}
//               />
//               <label
//                 id="checkBoxItems"
//                 htmlFor={checkboxId}
//                 className="dynamic-form__checkbox-label"
//               >
//                 {option}
//               </label>
//             </div>
//           );
//         })}
//       </div>
//     );
//   }

//   // 2. Select dropdown
//   if (fieldType === "String" && Array.isArray(fieldEnum)) {
//     return (
//       <div key={fieldName}>
//         <label htmlFor={fieldName}>{fieldName}</label>
//         <select
//           id={fieldName}
//           name={fieldName}
//           value={tempValue}
//           onChange={handleSelectChange}
//           disabled={DisableFields}
//         >
//           {fieldEnum.map((option) => (
//             <option key={option} value={option}>
//               {option}
//             </option>
//           ))}
//         </select>
//       </div>
//     );
//   }

//   // 3. Textarea or text input
//   return (
//     <div key={fieldName} className="editable-field-container">
//       <label htmlFor={fieldName}>{fieldName}</label>

//       {/* Feedback row: only one message at a time, always in the same place */}
//       <div className="character-feedback-row">
//         {showWarning ? (
//           <span className={`warning-message${shouldShowWarning ? " fade-in" : " fade-out"}`}>
//             Please write more   
//             {/* - {charsLeft > 0 ? charsLeft : ""} characters */}
//           </span>
//         ) : (
//           showCharacterCount && (
//             <span className="character-count">
//               {`${currentLength}/${characterLimit} characters used`}
//             </span>
//           )
//         )}
//       </div>

//       {characterLimit >= 1000 ? (
//         <div className="textarea-container">
//           <textarea
//             ref={inputRef}
//             id={fieldName}
//             name={fieldName}
//             value={isEditing ? tempValue : value}
//             onChange={handleChange}
//             onClick={startEditing}
//             maxLength={characterLimit}
//             readOnly={!isEditing}
//             className={`mediumBoxSize ${isEditing ? 'editing' : ''}`}
//             placeholder={placeHolder || "Introduce yourself and who you'd like to connect with and why..."}
//           />
//         </div>
//       ) : (
//         <div className="input-container">
//           <input
//             ref={inputRef}
//             type="text"
//             id={fieldName}
//             name={fieldName}
//             value={isEditing ? tempValue : value}
//             onChange={handleChange}
//             onClick={startEditing}
//             maxLength={20}
//             readOnly={!isEditing}
//             className={isEditing ? 'editing' : ''}
//             placeholder={placeHolder}
//           />
//         </div>
//       )}

//       {isEditing && (
//         <div className="edit-controls">
//           <button className="icon-button" onClick={handleSaveClick} type="button" aria-label="Save">
//             <FaSave />
//           </button>
//           <button className="icon-button" onClick={handleCancel} type="button" aria-label="Discard">
//             <FaTimes />
//           </button>
//         </div>
//       )}
//     </div>
//   );
// };

// export default EditableField;


// import React, { useState, useEffect, useRef } from "react";
// import { FaTimes, FaSave } from "react-icons/fa";
// import "./EditableFields.css"


// const EditableField = ({
//   fieldName,
//   fieldValue,
//   characterLimit,
//   characterMinimum,
//   placeHolder,
//   fieldType,
//   fieldEnum,
//   handleSave,
//   communityProfileName,
//   DisableFields,
// }) => {
//   const [value, setValue] = useState(fieldValue);
//   const [tempValue, setTempValue] = useState(fieldValue);
//   const [isEditing, setIsEditing] = useState(false);
//   const inputRef = useRef(null);

//   // For checkbox group
//   const [checkboxValues, setCheckboxValues] = useState(
//     Array.isArray(fieldValue) ? fieldValue : []
//   );

//   useEffect(() => {
//     if (Array.isArray(fieldValue)) {
//       setCheckboxValues(fieldValue);
//     }
//   }, [fieldValue]);

//   const handleChange = (event) => {
//     setTempValue(event.target.value);
//   };

//   const handleSelectChange = (event) => {
//     setTempValue(event.target.value);
//     handleSave(event, communityProfileName, fieldName);
//   };

//   const handleCheckboxChange = (event, option) => {
//     let updated;
//     if (event.target.checked) {
//       updated = [...checkboxValues, option];
//     } else {
//       updated = checkboxValues.filter((val) => val !== option);
//     }
//     setCheckboxValues(updated);

//     const syntheticEvent = {
//       target: { value: updated }
//     };
//     handleSave(syntheticEvent, communityProfileName, fieldName, option);
//   };

//   const startEditing = () => {
//     if (!DisableFields) {
//       setIsEditing(true);
//       setTimeout(() => inputRef.current?.focus(), 0);
//     }
//   };

//   const handleSaveClick = () => {
//     setValue(tempValue);
//     setIsEditing(false);
//     const syntheticEvent = { target: { value: tempValue } };
//     handleSave(syntheticEvent, communityProfileName, fieldName);
//   };

//   const handleCancel = () => {
//     setTempValue(value);
//     setIsEditing(false);
//   };

//   // --- Dynamic feedback logic ---
//   const currentLength = (isEditing ? tempValue : value).length;
//   const showWarning =
//     typeof characterMinimum === "number" &&
//     currentLength < characterMinimum &&
//     currentLength !== 0;
//   const showCharacterCount =
//     isEditing &&
//     typeof characterMinimum === "number" &&
//     currentLength >= characterMinimum;

//   // 1. Checkbox group
//   if (
//     Array.isArray(fieldType) &&
//     fieldType[0].type === "String" &&
//     fieldType[0].htmlref === "checkbox"
//   ) {
//     return (
//       <div key={fieldName} className="dynamic-form-checkboxes">
//         <label className="dynamic-form__checkbox-label">{fieldName}</label>
//         {fieldType[0].enum.map((option, index) => {
//           const checkboxId = `${fieldName}-${index}`;
//           return (
//             <div key={option} className="dynamic-form">
//               <input
//                 type="checkbox"
//                 id={checkboxId}
//                 name={fieldName}
//                 value={option}
//                 checked={checkboxValues.includes(option)}
//                 onChange={(event) => handleCheckboxChange(event, option)}
//                 disabled={DisableFields}
//               />
//               <label
//                 id="checkBoxItems"
//                 htmlFor={checkboxId}
//                 className="dynamic-form__checkbox-label"
//               >
//                 {option}
//               </label>
//             </div>
//           );
//         })}
//       </div>
//     );
//   }

//   // 2. Select dropdown
//   if (fieldType === "String" && Array.isArray(fieldEnum)) {
//     return (
//       <div key={fieldName}>
//         <label htmlFor={fieldName}>{fieldName}</label>
//         <select
//           id={fieldName}
//           name={fieldName}
//           value={tempValue}
//           onChange={handleSelectChange}
//           disabled={DisableFields}
//         >
//           {fieldEnum.map((option) => (
//             <option key={option} value={option}>
//               {option}
//             </option>
//           ))}
//         </select>
//       </div>
//     );
//   }

//   // 3. Textarea or text input
// return (
//   <div key={fieldName} className="editable-field-container">
//     <label htmlFor={fieldName}>{fieldName}</label>

//     {/* Show warning or character count, but only one at a time, on the same line above the box */}
//     <div className="character-feedback-row">
//       {showWarning ? (
//         <span className="warning-message">
//           Please write more!
//         </span>
//       ) : (
//         showCharacterCount && (
//           <span className="character-count">
//             {`${currentLength}/${characterLimit} characters used`}
//           </span>
//         )
//       )}
//     </div>

//     {characterLimit >= 1000 ? (
//       <div className="textarea-container">
//         <textarea
//           ref={inputRef}
//           id={fieldName}
//           name={fieldName}
//           value={isEditing ? tempValue : value}
//           onChange={handleChange}
//           onClick={startEditing}
//           maxLength={characterLimit}
//           readOnly={!isEditing}
//           className={`mediumBoxSize ${isEditing ? 'editing' : ''}`}
//           placeholder={placeHolder || "Introduce yourself and who you'd like to connect with and why..."}
//         />
//       </div>
//     ) : (
//       <div className="input-container">
//         <input
//           ref={inputRef}
//           type="text"
//           id={fieldName}
//           name={fieldName}
//           value={isEditing ? tempValue : value}
//           onChange={handleChange}
//           onClick={startEditing}
//           maxLength={20}
//           readOnly={!isEditing}
//           className={isEditing ? 'editing' : ''}
//           placeholder={placeHolder}
//         />
//       </div>
//     )}

//     {isEditing && (
//       <div className="edit-controls">
//         <button className="icon-button" onClick={handleSaveClick} type="button" aria-label="Save">
//           <FaSave />
//         </button>
//         <button className="icon-button" onClick={handleCancel} type="button" aria-label="Discard">
//           <FaTimes />
//         </button>
//       </div>
//     )}
//   </div>
// );


// };

// export default EditableField;


//working well

// import React, { useState, useEffect } from "react";

// /**
//  * EditableField
//  * 
//  * Renders an editable field for a profile form.
//  * Supports text input, textarea, select dropdown, and checkbox group.
//  * Handles local editing state, save/discard logic, and character count.
//  * 
//  * Props:
//  * - fieldName: String
//  * - fieldValue: String | Array
//  * - characterLimit: Number
//  * - fieldType: String | Array
//  * - fieldEnum: Array (for select or checkbox)
//  * - handleSave: Function (event, communityProfileName, fieldName, option?)
//  * - communityProfileName: String
//  * - DisableFields: Boolean
//  */
// const EditableField = ({
//   fieldName,
//   fieldValue,
//   characterLimit,
//   fieldType,
//   fieldEnum,
//   handleSave,
//   communityProfileName,
//   DisableFields,
// }) => {
//   // For text/textarea/select: use local temp state
//   const [value, setValue] = useState(fieldValue);
//   const [tempValue, setTempValue] = useState(fieldValue);

//   // For checkbox group: manage local array state if needed
//   const [checkboxValues, setCheckboxValues] = useState(
//     Array.isArray(fieldValue) ? fieldValue : []
//   );

//   // Keep checkboxValues in sync with fieldValue prop
//   useEffect(() => {
//     if (Array.isArray(fieldValue)) {
//       setCheckboxValues(fieldValue);
//     }
//   }, [fieldValue]);

//   // Handle text/textarea change
//   const handleChange = (event) => setTempValue(event.target.value);

//   // Handle select change
//   const handleSelectChange = (event) => {
//     setTempValue(event.target.value);
//     handleSave(event, communityProfileName, fieldName);
//   };

//   // Handle checkbox change
//   const handleCheckboxChange = (event, option) => {
//     let updated;
//     if (event.target.checked) {
//       updated = [...checkboxValues, option];
//     } else {
//       updated = checkboxValues.filter((val) => val !== option);
//     }
//     setCheckboxValues(updated);

//     // Create a synthetic event for consistency
//     const syntheticEvent = {
//       target: { value: updated }
//     };
//     // Call handleSave immediately so backend is updated
//     handleSave(syntheticEvent, communityProfileName, fieldName, option);
//   };

//   // Discard changes for text/textarea
//   const handleCancel = () => {
//     setTempValue(value);
//   };

//   // Save for text/textarea
//   const handleSaveClick = () => {
//     setValue(tempValue);
//     const syntheticEvent = {
//       target: { value: tempValue }
//     };
//     handleSave(syntheticEvent, communityProfileName, fieldName);
//   };

//   // Render logic
//   // 1. Checkbox group
//   if (
//     Array.isArray(fieldType) &&
//     fieldType[0].type === "String" &&
//     fieldType[0].htmlref === "checkbox"
//   ) {
//     return (
//       <div key={fieldName} className="dynamic-form-checkboxes">
//         <label className="dynamic-form__checkbox-label">{fieldName}</label>
//         {fieldType[0].enum.map((option, index) => {
//           const checkboxId = `${fieldName}-${index}`;
//           return (
//             <div key={option} className="dynamic-form">
//               <input
//                 type="checkbox"
//                 id={checkboxId}
//                 name={fieldName}
//                 value={option}
//                 checked={checkboxValues.includes(option)}
//                 onChange={(event) => handleCheckboxChange(event, option)}
//                 disabled={DisableFields}
//               />
//               <label
//                 id="checkBoxItems"
//                 htmlFor={checkboxId}
//                 className="dynamic-form__checkbox-label"
//               >
//                 {option}
//               </label>
//             </div>
//           );
//         })}
//       </div>
//     );
//   }

//   // 2. Select dropdown
//   if (fieldType === "String" && Array.isArray(fieldEnum)) {
//     return (
//       <div key={fieldName}>
//         <label htmlFor={fieldName}>{fieldName}</label>
//         <select
//           id={fieldName}
//           name={fieldName}
//           value={tempValue}
//           onChange={handleSelectChange}
//           disabled={DisableFields}
//         >
//           {fieldEnum.map((option) => (
//             <option key={option} value={option}>
//               {option}
//             </option>
//           ))}
//         </select>
//       </div>
//     );
//   }

//   // 3. Textarea or text input
//   return (
//     <div key={fieldName}>
//       <label htmlFor={fieldName}>{fieldName}</label>
//       {characterLimit >= 1000 ? (
//         <textarea
//           id={fieldName}
//           name={fieldName}
//           value={tempValue}
//           onChange={handleChange}
//           maxLength={characterLimit}
//           readOnly={DisableFields}
//           className="mediumBoxSize"
//           placeholder={tempValue ? "" : "Introduce yourself and who you'd like to connect with and why..."}
//         />
//       ) : (
//         <input
//           type="text"
//           id={fieldName}
//           name={fieldName}
//           value={tempValue}
//           onChange={handleChange}
//           maxLength={20}
//           readOnly={DisableFields}
//         />
//       )}
//       {!DisableFields && (
//         <>
//           <button onClick={handleSaveClick}>Save</button>
//           <button onClick={handleCancel}>Discard</button>
//         </>
//       )}
//       <div>
//         {`${tempValue.length}/${characterLimit >= 20 ? characterLimit : 20} characters used`}
//       </div>
//     </div>
//   );
// };

// export default EditableField;
